import React, { useEffect, useMemo } from "react";
import { IS_VATAR } from "../vatar/env";
import { postScore } from "../vatar/vatarClient";
import { useNavigate } from "react-router-dom";
import gameStatsService from "../Services/gameStatsService";
import analytics from "../Services/analyticsService";
import { Home } from "lucide-react";
import RematchPanel from "../Components/RematchPanel";
import ShareResultCard from "../Components/ShareResultCard";
import { useMyMascot } from "../Components/Mascot";
import { normalizeMascot } from "../Components/Mascot/mascotMood";
import { firstName, roomCard, roomCardCopy } from "../share/resultCards";
import { roundBreakdown, gameHighlights, joinNames } from "../utils/gameHighlights";

const FinalResults = ({ standings, roomData, roomDocId, currentUserId }) => {
  const navigate = useNavigate();
  const myMascot = useMyMascot();

  const winner = standings[0];

  // Vatar leaderboard: this player's total for the finished game.
  useEffect(() => {
    if (!IS_VATAR) return;
    const mine = standings.find((s) => s.id === currentUserId);
    if (mine) postScore(mine.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Built once when the game ends, so the featured answer doesn't change under the player.
  const card = useMemo(
    () => roomCard({ room: roomData, standings, meId: currentUserId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [standings.length]
  );
  const cardArt = useMemo(() => {
    const art = {};
    const add = (id, name, mood) => {
      if (!id || art[id]) return;
      const mascot = id === currentUserId ? myMascot : normalizeMascot(roomData?.mascots?.[id]);
      art[id] = { mascot, mood, letter: firstName(name)[0] };
    };
    (card.ranked || []).slice(0, 3).forEach((p) => add(p.id, p.name, p.place === 1 ? "celebrate" : "happy"));
    card.podium.forEach((p) => add(p.id, p.name, p.place === 1 ? "celebrate" : "happy"));
    card.votedOut.forEach((v) => add(v.id, v.name, "sad"));
    return art;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card]);

  const handleRunWarmup = () => {
    analytics.track("run_entry_tapped", { source: "room_results" });
    navigate("/run", { state: { from: "room_results" } });
  };

  // Add stats tracking when component mounts
  useEffect(() => {
    const updatePlayerStats = async () => {
      if (!standings || standings.length === 0) return;

      try {
        // Get current player's name from localStorage
        const currentPlayerName = localStorage.getItem("playerName");
        if (!currentPlayerName) return;

        // Find user's position in standings
        const userPosition = standings.findIndex(
          (player) => player.name === currentPlayerName
        );

        if (userPosition === -1) return; // User not found in standings

        const isWinner = userPosition === 0; // First place is winner

        await gameStatsService.updateUserStats({
          isWinner,
          totalPlayers: standings.length,
          finalPosition: userPosition + 1, // Convert to 1-based
        });

        console.log("📊 Web player stats updated after game completion");
      } catch (error) {
        console.error("❌ Failed to update web player stats:", error);
      }
    };

    updatePlayerStats();
  }, [standings]); // Trigger when standings are available

  const players = standings.map((s) => ({ id: s.id, name: s.name }));
  const breakdown = roundBreakdown(roomData, players);
  const highlights = gameHighlights(roomData, players);
  const roundsTotal = (id) => breakdown.reduce((sum, r) => sum + (r.points[id] ?? 0), 0);
  const hasPowerUpPoints = standings.some((s) => s.total !== roundsTotal(s.id));
  const topThree = standings.slice(0, 3).map((player, i) => ({ player, place: i + 1 }));
  const podium =
    topThree.length === 3 ? [topThree[1], topThree[0], topThree[2]]
    : topThree.length === 2 ? [topThree[1], topThree[0]]
    : topThree;
  const podiumStyle = {
    1: { height: 190, color: "bg-yellow-600" },
    2: { height: 140, color: "bg-gray-500" },
    3: { height: 100, color: "bg-orange-600" },
  };
  const lead = standings.length > 1 ? winner.total - standings[1].total : 0;
  const highlightCards = [
    highlights.mostUnique && {
      label: "Most unique answers",
      who: joinNames(highlights.mostUnique.names),
      detail:
        highlights.mostUnique.names.length > 1
          ? `${highlights.mostUnique.count} unique answers each`
          : `${highlights.mostUnique.count} answer${highlights.mostUnique.count === 1 ? "" : "s"} nobody else had`,
    },
    highlights.bestRound && {
      label: "Best round",
      who: joinNames(highlights.bestRound.names),
      detail: `${highlights.bestRound.points} points in round ${highlights.bestRound.round}${highlights.bestRound.letter ? ` (letter ${highlights.bestRound.letter})` : ""}`,
    },
    highlights.closestCall && {
      label: "Closest call",
      who: highlights.closestCall.answer,
      detail: `${highlights.closestCall.name}'s ${highlights.closestCall.category}, decided by a coin flip`,
    },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-orange-50 container px-6 py-8 lg:mx-auto">
      <div className="max-w-4xl lg:max-w-6xl mx-auto text-center">
        <h1 className="text-2xl md:text-4xl font-bold text-red-800 mb-8 lg:mb-2 lg:text-5xl lg:text-left">
          {winner.name} WON!
        </h1>
        {standings.length > 1 && (
          <p className="mb-6 -mt-5 text-gray-600 lg:mt-0 lg:mb-8 lg:text-left">
            {lead > 0 ? `${lead} points clear of ${standings[1].name}` : `Level on points with ${standings[1].name}`}
          </p>
        )}
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-6 lg:items-start">
        <div className="min-w-0">

        <div className="bg-white rounded-lg shadow-lg p-5 mb-6 lg:p-8 lg:mb-8">
          <h2 className="text-2xl font-semibold mb-6">Final Standings</h2>

          <div className="flex justify-center items-end gap-2 lg:gap-4 mb-2">
            {podium.map(({ player, place }) => (
              <div key={player.id ?? player.name} className="flex-1 min-w-0 max-w-36 flex flex-col items-center gap-1.5">
                <span className="max-w-full truncate text-base lg:text-lg font-extrabold">{player.name}</span>
                <span className="text-lg lg:text-xl font-black text-red-600">{player.total} pts</span>
                <div
                  className={`w-full rounded-t-xl pt-3 flex items-start justify-center text-3xl font-black text-white h-[calc(var(--h)*0.7)] lg:h-[var(--h)] ${podiumStyle[place].color}`}
                  style={{ "--h": `${podiumStyle[place].height}px` }}
                >
                  {place}
                </div>
              </div>
            ))}
          </div>

          {/* Complete standings */}
          <div className={`max-w-md mx-auto ${standings.length <= 3 ? "hidden" : ""}`}>
            <h3 className="text-lg font-semibold mb-4">Complete Results</h3>
            {standings.map((player, index) => (
              <div
                key={player.name}
                className="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg font-bold">{index + 1}.</span>
                  <span>{player.name}</span>
                </div>
                <span className="font-bold text-red-600">
                  {player.total} pts
                </span>
              </div>
            ))}
          </div>

          {!IS_VATAR && standings.length > 0 && (
            <div className="mt-6 flex justify-center">
              <ShareResultCard
                compact
                type="room"
                card={card}
                options={["standings"]}
                getCopy={(kind, pick) => roomCardCopy(card, kind, pick)}
                art={cardArt}
              />
            </div>
          )}
        </div>

        </div>

        <div className="min-w-0 text-left">
        {breakdown.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-4 lg:p-5 mb-4 overflow-x-auto">
            <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">Round by round</h2>
            <table className="w-full text-[15px]">
              <thead>
                <tr className="text-sm text-gray-600">
                  <th scope="col" className="py-2 pr-2 text-left font-bold">Round</th>
                  {standings.map((p) => (
                    <th scope="col" key={p.id ?? p.name} className="py-2 px-2 text-right font-bold">
                      <span className="block max-w-[4rem] lg:max-w-[5.5rem] truncate ml-auto">{p.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {breakdown.map((r) => {
                  const best = Math.max(...Object.values(r.points));
                  return (
                    <tr key={r.round} className="border-t border-gray-100">
                      <th scope="row" className="py-2.5 pr-2 text-left font-semibold">
                        {r.round}{r.letter ? ` · ${r.letter}` : ""}
                      </th>
                      {standings.map((p) => {
                        const pts = r.points[p.id] ?? 0;
                        return (
                          <td key={p.id ?? p.name} className={`py-2.5 px-2 text-right ${pts === best && pts > 0 ? "font-extrabold text-gray-900" : "text-gray-600"}`}>
                            {pts}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {hasPowerUpPoints && (
                  <tr className="border-t border-gray-100">
                    <th scope="row" className="py-2.5 pr-2 text-left font-semibold">Power-ups</th>
                    {standings.map((p) => {
                      const extra = p.total - roundsTotal(p.id);
                      return (
                        <td key={p.id ?? p.name} className="py-2.5 px-2 text-right text-gray-600">
                          {extra > 0 ? `+${extra}` : extra}
                        </td>
                      );
                    })}
                  </tr>
                )}
                <tr className="border-t-2 border-gray-200">
                  <th scope="row" className="py-2.5 pr-2 text-left font-extrabold">Total</th>
                  {standings.map((p) => (
                    <td key={p.id ?? p.name} className="py-2.5 px-2 text-right text-lg font-black text-red-600">
                      {p.total}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {highlightCards.length > 0 && (
          <div
            className="grid gap-3 mb-4 lg:[grid-template-columns:var(--cols)]"
            style={{ "--cols": `repeat(${highlightCards.length}, minmax(0, 1fr))` }}
          >
            {highlightCards.map((h) => (
              <div key={h.label} className="bg-white rounded-lg shadow-md p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{h.label}</p>
                <p className="mt-1.5 text-lg font-extrabold text-gray-900 break-words">{h.who}</p>
                <p className="mt-0.5 text-sm text-gray-600">{h.detail}</p>
              </div>
            ))}
          </div>
        )}

        {!IS_VATAR && (
          <RematchPanel
            roomData={roomData}
            roomDocId={roomDocId}
            currentUserId={currentUserId}
          />
        )}
        <div className="flex gap-4">
          <button
            onClick={() => {
              localStorage.removeItem("gameState");
              navigate("/");
            }}
            className="flex-1 bg-gray-800 text-white font-semibold px-8 py-4 rounded-xl hover:bg-gray-900 transition duration-300 shadow-lg flex items-center justify-center gap-2"
          >
            <Home size={20} />
            Back home
          </button>
        </div>
        {!IS_VATAR && (
          <button
            type="button"
            onClick={handleRunWarmup}
            className="mt-3 text-sm font-semibold text-red-700 hover:text-red-800"
          >
            Warm up with NameIt Run →
          </button>
        )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default FinalResults;
